import { useLazyQuery } from '@apollo/client';
import { Box } from '@ovComponents/1-primative/box/box';
import { Skeleton } from '@ovComponents/1-primative/skeleton/skeleton';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { Chip } from '@ovComponents/2-component/chip/chip';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { LineChart } from '@ovComponents/2-component/lineChart/lineChart';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { historyDailyTypes } from '@providers/statsHooks';
import { useThemeTokens } from '@providers/themeTokenProvider';
import type { ColumnDef } from '@tanstack/react-table';
import { formatMoneyValue } from '@utils/moneyFormatting';
import type React from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { SortIndicator, useTradingTable } from '../hooks/useTradingTable';
import {
  FETCH_PORTFOLIO_STATS_HISTORY,
  FETCH_SUB_TRADE_REQUESTS_FULL,
} from '../queries';
import {
  ALIGNED_TABLE_SX,
  BOLD_SX,
  BORDERED_BOX_SX,
  LABEL_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  SORTABLE_HEADER_SX,
  TABLE_HEAD_SX,
  TABULAR_SX,
  TRUNCATE_SX,
  VALUE_ROW_SX,
} from '../styles';
import {
  calculateDrift,
  formatMoney,
  getPortfolioName,
  valueColor,
} from '../utils';
import SecurityHistoryPanel from './securityHistoryPanel';

const HOLDING_TABLE_COLUMNS: Array<{
  id: string;
  label: string;
  width: string;
  minWidth: string;
  number?: boolean;
  accessorFn: (r: any) => any;
}> = [
  {
    id: 'symbol',
    label: 'Symbol',
    width: '20%',
    minWidth: '140px',
    accessorFn: (r) => r.financialProduct?.ticker || r.currency || 'Cash',
  },
  {
    id: 'quantity',
    label: 'Qty',
    width: '12%',
    minWidth: '80px',
    number: true,
    accessorFn: (r) => r.quantity || 0,
  },
  {
    id: 'price',
    label: 'Price',
    width: '12%',
    minWidth: '80px',
    number: true,
    accessorFn: (r) => r.financialProduct?.currentPriceCents || 0,
  },
  {
    id: 'value',
    label: 'Value',
    width: '13%',
    minWidth: '90px',
    number: true,
    accessorFn: (r) => r.valueCents || 0,
  },
  {
    id: 'curPct',
    label: 'Curr %',
    width: '10%',
    minWidth: '65px',
    number: true,
    accessorFn: (r) => r.currentPct,
  },
  {
    id: 'tgtPct',
    label: 'Tgt %',
    width: '10%',
    minWidth: '65px',
    number: true,
    accessorFn: (r) => r.targetPct,
  },
  {
    id: 'driftPct',
    label: 'Drift',
    width: '11%',
    minWidth: '70px',
    number: true,
    accessorFn: (r) => r.driftPct,
  },
  {
    id: 'driftVal',
    label: 'Drift $',
    width: '12%',
    minWidth: '80px',
    number: true,
    accessorFn: (r) => r.driftCents,
  },
];

const LINE_CHART_OPTIONS = {
  plugins: {
    tooltip: {
      position: 'nearest' as const,
      callbacks: {
        label: (ctx: any) =>
          `${ctx.dataset.label}: ${formatMoneyValue(ctx.raw, '$', 2)}`,
      },
    },
  },
  scales: {
    x: {
      display: true,
      grid: { display: false },
      border: { display: true },
      ticks: { maxTicksLimit: 6, maxRotation: 0, font: { size: 9 } },
    },
    y: {
      display: true,
      position: 'right' as const,
      grid: { display: false },
      border: { display: true },
      ticks: {
        maxTicksLimit: 4,
        callback: (v: any) => `$${Math.floor(+v / 100).toLocaleString()}`,
        font: { size: 9 },
      },
    },
  },
};

const StatBlock = ({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) => (
  <Box flex={1} textAlign='center'>
    <Typography variant='labelSmall' sx={SECTION_HEADER_SX}>
      {label}
    </Typography>
    <Typography variant='labelSmall' sx={{ ...BOLD_SX, color }}>
      {value >= 0 ? '+' : ''}
      {formatMoneyValue(value, '$', 2)}
    </Typography>
  </Box>
);

const FlowRow = ({
  label,
  value,
  color,
  onSurface,
}: {
  label: string;
  value: number;
  color?: string;
  onSurface?: string;
}) => (
  <Box display='flex' justifyContent='space-between'>
    <Typography variant='bodySmall' sx={SECONDARY_TEXT_SX}>
      {label}
    </Typography>
    <Typography
      variant='bodySmall'
      sx={{
        ...VALUE_ROW_SX,
        color: color ?? onSurface,
      }}
    >
      {value >= 0 ? '+' : ''}
      {formatMoneyValue(value, '$', 2)}
    </Typography>
  </Box>
);

const SubAcctStat = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Box>
    <Typography
      variant='labelSmall'
      sx={{ ...SECTION_HEADER_SX, ...(color ? { color } : {}) }}
    >
      {label}
    </Typography>
    <Typography
      variant='labelSmall'
      sx={color ? { color } : { fontWeight: 600 }}
    >
      {value}
    </Typography>
  </Box>
);

const BoldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant='labelSmall' sx={BOLD_SX}>
    {children}
  </Typography>
);
const HoldingCell = ({
  w,
  n,
  children,
  sx,
}: {
  w: string;
  n?: boolean;
  children: React.ReactNode;
  sx?: object;
}) => (
  <TableCell number={n} sx={{ width: w, padding: '4px 16px', ...sx }}>
    {children}
  </TableCell>
);

export const openClientDetails = (report: any) => {
  const userId =
    report.modelPortfolioType === 'GOAL'
      ? report.object.user?.id
      : report.object.account?.user?.id;
  if (userId) window.open(`/clients/${userId}`, '_blank');
};

const PortfolioRowDetail = ({
  report,
  onShowTradeBlotter,
}: {
  report: any;
  onShowTradeBlotter: () => void;
}) => {
  const { sys } = useThemeTokens();
  const [expandedHoldingId, setExpandedHoldingId] = useState<string | null>(
    null,
  );

  const enrichedHoldings = useMemo(() => {
    if (!report.holdings || report.holdings.length === 0) return [];
    const mv = report.marketValueCents || 1;
    return report.holdings.map((h: any) => {
      const currentPct = ((h.valueCents || 0) / mv) * 100;
      const targetPct = (h.expectedPercentage || 0) * 100;
      const driftPct = currentPct - targetPct;
      const driftCents = (h.valueCents || 0) - (h.expectedValueCents || 0);
      const sortGroup = h.financialProduct?.isCash ? 0 : 1;
      return { ...h, currentPct, targetPct, driftPct, driftCents, sortGroup };
    });
  }, [report.holdings, report.marketValueCents]);

  const holdingColumns: ColumnDef<any>[] = useMemo(
    () =>
      HOLDING_TABLE_COLUMNS.map((c) => ({
        id: c.id,
        header: c.label,
        accessorFn: c.accessorFn,
      })),
    [],
  );

  const { table: holdingTable } = useTradingTable({
    data: enrichedHoldings,
    columns: holdingColumns,
    initialSorting: [{ id: 'value', desc: true }],
  });

  const [chartHistory, setChartHistory] = useState<any[]>([]);
  const [fetchChartHistory, { loading: chartLoading, error: chartError }] =
    useLazyQuery(FETCH_PORTFOLIO_STATS_HISTORY, {
      onCompleted: (d: any) => {
        const snapshots = d?.fetchStatsHistory?.snapshots || [];
        setChartHistory(snapshots);
      },
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    });

  // Auto-fetch chart data when report identity or type/currency change
  useEffect(() => {
    const objectId = report.object?.id;
    if (!objectId) return;

    const objectType =
      report.modelPortfolioType === 'GOAL' ? 'Goal' : 'SubAccount';
    const historyType = historyDailyTypes[objectType];

    fetchChartHistory({
      variables: {
        input: {
          type: historyType,
          id: objectId,
          currency: report.baseCurrency || 'CAD',
        },
      },
    });
  }, [
    report.object?.id,
    report.modelPortfolioType,
    report.baseCurrency,
    fetchChartHistory,
  ]);

  const [
    fetchPendingTrades,
    {
      data: pendingTradesData,
      loading: pendingTradesLoading,
      called: pendingTradesCalled,
    },
  ] = useLazyQuery(FETCH_SUB_TRADE_REQUESTS_FULL, {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  const pendingTrades: any[] =
    pendingTradesData?.fetchSubTradeRequests?.subTradeRequests || [];

  useEffect(() => {
    const isGoal = report.modelPortfolioType === 'GOAL';
    const objectId = report.object?.id;
    if (!objectId) return;
    fetchPendingTrades({
      variables: {
        input: {
          filter: {
            ...(isGoal ? { goalId: objectId } : { subAccountId: objectId }),
            states: ['INITIATED', 'READY', 'REQUESTED'],
          },
          pagination: { perPage: 50, offSet: 0 },
        },
      },
    });
  }, [report.object?.id, report.modelPortfolioType, fetchPendingTrades]);

  // Resolve theme colors for Chart.js (Chart.js needs real CSS colors, not MUI tokens)
  const chartColors = useMemo(
    () => ({
      primary: sys.color.primary,
      primaryBg: sys.color.primaryVariant,
      textSecondary: sys.color.onSurfaceVariant,
      success: sys.color.positive,
      error: sys.color.negative,
    }),
    [sys],
  );

  const { lineChartData, barChartLabels, barChartDatasets } = useMemo(() => {
    if (!chartHistory || chartHistory.length === 0) {
      return {
        lineChartData: null,
        barChartLabels: [] as string[],
        barChartDatasets: [] as any[],
      };
    }
    const step = Math.max(1, Math.floor(chartHistory.length / 60));
    const sampled = chartHistory.filter(
      (_: any, i: number) => i % step === 0 || i === chartHistory.length - 1,
    );
    const dateLabels = sampled.map((s: any) => {
      const d = new Date(s.date);
      return `${d.getMonth() + 1}/${d.getDate()}`;
    });
    const lineData: any = {
      labels: dateLabels,
      datasets: [
        {
          fill: true,
          label: 'Market Value',
          data: sampled.map((s: any) => s.marketValueCents),
          borderColor: chartColors.primary,
          backgroundColor: chartColors.primaryBg,
          tension: 0.3,
          borderWidth: 1.5,
          pointRadius: 0,
        },
        {
          fill: false,
          label: 'Net Contributions',
          data: sampled.map((s: any) => s.netContributionCents),
          borderColor: chartColors.textSecondary,
          borderDash: [4, 3],
          backgroundColor: 'transparent',
          tension: 0.3,
          borderWidth: 1,
          pointRadius: 0,
        },
      ],
    };
    const monthMap = new Map<string, { first: number; last: number }>();
    chartHistory.forEach((s: any) => {
      const d = new Date(s.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const existing = monthMap.get(key);
      if (!existing) {
        monthMap.set(key, {
          first: s.netContributionCents,
          last: s.netContributionCents,
        });
      } else {
        existing.last = s.netContributionCents;
      }
    });
    const monthKeys = Array.from(monthMap.keys()).sort();
    const monthNamesArr = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const monthLabels = monthKeys.map((k) => {
      const [y, m] = k.split('-');
      return `${monthNamesArr[parseInt(m, 10) - 1]} '${y.slice(2)}`;
    });
    const monthlyFlows: number[] = [];
    for (let i = 0; i < monthKeys.length; i++) {
      const current = monthMap.get(monthKeys[i])!;
      if (i === 0) {
        monthlyFlows.push(current.last - current.first);
      } else {
        const prev = monthMap.get(monthKeys[i - 1])!;
        monthlyFlows.push(current.last - prev.last);
      }
    }
    const barDatasets = [
      {
        label: 'Monthly Net Flow',
        data: monthlyFlows,
        backgroundColor: monthlyFlows.map((v) =>
          v >= 0 ? chartColors.success : chartColors.error,
        ),
        borderColor: monthlyFlows.map((v) =>
          v >= 0 ? chartColors.success : chartColors.error,
        ),
        borderWidth: 1,
      },
    ];
    return {
      lineChartData: lineData,
      barChartLabels: monthLabels,
      barChartDatasets: barDatasets,
    };
  }, [chartHistory, chartColors]);

  const driftPct = calculateDrift(report);

  return (
    <TableRow>
      <TableCell sx={{ py: 0, borderBottom: 'none' }} colSpan={9}>
        <Box sx={{ py: 1.5, px: 2, backgroundColor: sys.color.neutralHover }}>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
            mb={1.5}
          >
            <Box display='flex' alignItems='center' gap={1}>
              <Typography variant='titleMedium' weight='bold'>
                {getPortfolioName(report)}
              </Typography>
              <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
                {report.modelPortfolio?.translatedName?.en || 'No Model'}
                {report.modelPortfolioType === 'GOAL' && report.object.type
                  ? ` • ${report.object.type}`
                  : ''}
                {report.modelPortfolioType === 'SUB_ACCOUNT' &&
                report.object.account?.type
                  ? ` • ${report.object.account.type} (Tax Rank: ${report.object.account.taxRank})`
                  : ''}
              </Typography>
              {report.originalCurrencies &&
                report.originalCurrencies.length > 1 && (
                  <Chip
                    text={`Multi-CCY (${report.originalCurrencies.join(', ')})`}
                    small
                  />
                )}
            </Box>
            <Box display='flex' alignItems='center' gap={2}>
              <Typography
                variant='bodySmall'
                sx={{ color: sys.color.onSurfaceVariant }}
              >
                UPD: {new Date(report.updatedAt).toLocaleString()}
              </Typography>
            </Box>
          </Box>

          <Box
            display='grid'
            gridTemplateColumns={{ xs: '1fr', md: '2fr 1fr' }}
            gap={2}
            mb={2}
          >
            {/* Market Value + Contributions Line Chart */}
            <Box
              sx={{ ...BORDERED_BOX_SX, bgcolor: sys.color.surface, p: 1.5 }}
            >
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                mb={1}
              >
                <Typography variant='labelSmall' sx={SECTION_HEADER_SX}>
                  Market Value vs Contributions (6mo)
                </Typography>
                <Box display='flex' gap={1.5}>
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <Box
                      sx={{
                        width: 16,
                        height: 2,
                        bgcolor: sys.color.primary,
                        borderRadius: 1,
                      }}
                    />
                    <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
                      MV
                    </Typography>
                  </Box>
                  <Box display='flex' alignItems='center' gap={0.5}>
                    <Box
                      sx={{
                        width: 16,
                        height: 2,
                        bgcolor: sys.color.onSurfaceVariant,
                        borderRadius: 1,
                        borderTop: '1px dashed',
                        borderTopColor: sys.color.onSurfaceVariant,
                      }}
                    />
                    <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
                      Contributions
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ height: 180, position: 'relative' }}>
                {chartLoading && (
                  <Box
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 1,
                      bgcolor: sys.color.surface,
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
                {lineChartData ? (
                  <LineChart
                    labels={[]}
                    overrideData={lineChartData}
                    showAxis={true}
                    format='CENTS'
                    overrideOptions={LINE_CHART_OPTIONS}
                  />
                ) : !chartLoading ? (
                  <Box
                    display='flex'
                    flexDirection='column'
                    alignItems='center'
                    justifyContent='center'
                    height='100%'
                    gap={0.5}
                  >
                    <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
                      No history data available
                    </Typography>
                    {chartError && (
                      <Typography
                        variant='labelSmall'
                        sx={{ color: sys.color.negative }}
                      >
                        {chartError.message}
                      </Typography>
                    )}
                  </Box>
                ) : null}
              </Box>
              {lineChartData &&
                chartHistory.length > 1 &&
                (() => {
                  const f = chartHistory[0],
                    l = chartHistory[chartHistory.length - 1];
                  const mvChange = l.marketValueCents - f.marketValueCents;
                  const contribChange =
                    l.netContributionCents - f.netContributionCents;
                  return (
                    <Box
                      display='flex'
                      gap={2}
                      mt={1}
                      pt={1}
                      sx={{
                        borderTop: '1px solid',
                        borderTopColor: sys.color.outlineVariant,
                      }}
                    >
                      <StatBlock
                        label='MV Change'
                        value={mvChange}
                        color={valueColor(sys.color, mvChange)}
                      />
                      <StatBlock
                        label='Net Flows'
                        value={contribChange}
                        color={valueColor(sys.color, contribChange)}
                      />
                      <StatBlock
                        label='Inv. Return'
                        value={mvChange - contribChange}
                        color={valueColor(sys.color, mvChange - contribChange)}
                      />
                    </Box>
                  );
                })()}
            </Box>

            {/* Portfolio Summary + Cash Flows */}
            <Box
              sx={{
                ...BORDERED_BOX_SX,
                bgcolor: sys.color.surface,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {[
                {
                  label: 'Market Value',
                  value: formatMoney(report.marketValueCents),
                  color: sys.color.onSurface,
                },
                {
                  label: 'Cash',
                  value: `${formatMoney(report.cashAvailableForTradeCents)} (${report.marketValueCents > 0 ? ((report.cashAvailableForTradeCents / report.marketValueCents) * 100).toFixed(1) : '0.0'}%)`,
                  color: sys.color.onSurface,
                },
                {
                  label: 'Target Cash',
                  value: `${((report.expectedCashPercentage || 0) * 100).toFixed(1)}% · ${formatMoney(report.expectedCashCents || 0)}`,
                  color: sys.color.onSurface,
                },
                {
                  label: 'Drift',
                  value:
                    driftPct !== null
                      ? `${driftPct.toFixed(1)}% · ${driftPct > 5 ? 'Exceeds threshold' : driftPct > 2 ? 'Monitor' : 'Within tolerance'}`
                      : '-',
                  color:
                    driftPct !== null && driftPct > 5
                      ? sys.color.negative
                      : driftPct !== null && driftPct > 2
                        ? sys.color.warning
                        : sys.color.positive,
                },
                {
                  label: 'Pending',
                  value: (() => {
                    if (pendingTradesCalled && !pendingTradesLoading) {
                      if (
                        pendingTrades.length === 0 &&
                        report.pendingSubTradeRequests > 0
                      )
                        return `0 active (report: ${report.pendingSubTradeRequests} stale)`;
                      if (pendingTrades.length > 0)
                        return `${pendingTrades.length} trades`;
                      return 'None';
                    }
                    return report.pendingSubTradeRequests > 0
                      ? `${report.pendingSubTradeRequests} trades`
                      : 'None';
                  })(),
                  color: (() => {
                    if (
                      pendingTradesCalled &&
                      !pendingTradesLoading &&
                      pendingTrades.length === 0 &&
                      report.pendingSubTradeRequests > 0
                    )
                      return sys.color.warning;
                    if (
                      (pendingTradesCalled && !pendingTradesLoading
                        ? pendingTrades.length
                        : report.pendingSubTradeRequests) > 0
                    )
                      return sys.color.warning;
                    return sys.color.onSurfaceVariant;
                  })(),
                },
                {
                  label: 'Holdings',
                  value: `${report.holdings?.length || 0} positions`,
                  color: sys.color.onSurface,
                },
              ].map((row) => (
                <Box
                  key={row.label}
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                >
                  <Typography
                    variant='bodySmall'
                    sx={{ color: sys.color.onSurfaceVariant }}
                  >
                    {row.label}
                  </Typography>
                  <Typography
                    variant='bodySmall'
                    sx={{ ...VALUE_ROW_SX, color: row.color }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              ))}

              {/* Cash Flows */}
              {barChartDatasets.length > 0 &&
                barChartDatasets[0].data.length > 0 &&
                (() => {
                  const d = barChartDatasets[0].data;
                  const totalInflows = d
                    .filter((v: number) => v > 0)
                    .reduce((s: number, v: number) => s + v, 0);
                  const totalOutflows = d
                    .filter((v: number) => v < 0)
                    .reduce((s: number, v: number) => s + v, 0);
                  const net = totalInflows + totalOutflows;
                  return (
                    <Box
                      sx={{
                        borderTop: '1px solid',
                        borderColor: sys.color.outlineVariant,
                        pt: 1,
                        mt: 0.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                      }}
                    >
                      <FlowRow
                        label='Inflows'
                        value={totalInflows}
                        color={sys.color.positive}
                        onSurface={sys.color.onSurface}
                      />
                      <FlowRow
                        label='Outflows'
                        value={totalOutflows}
                        color={sys.color.negative}
                        onSurface={sys.color.onSurface}
                      />
                      <FlowRow
                        label={`Net (${barChartLabels.length}mo)`}
                        value={net}
                        color={
                          net >= 0 ? sys.color.positive : sys.color.negative
                        }
                        onSurface={sys.color.onSurface}
                      />
                    </Box>
                  );
                })()}
            </Box>
          </Box>

          {/* Pending Trades — always fetch; hide only once confirmed empty */}
          {!(
            pendingTradesCalled &&
            !pendingTradesLoading &&
            pendingTrades.length === 0
          ) && (
            <Box mb={2}>
              <Typography
                variant='labelSmall'
                sx={{ mb: 1, ...SECTION_HEADER_SX }}
              >
                Pending Trades
                {pendingTradesCalled && !pendingTradesLoading
                  ? ` (${pendingTrades.length})`
                  : ''}
              </Typography>
              <Box
                sx={{
                  ...BORDERED_BOX_SX,
                  bgcolor: sys.color.surface,
                  overflow: 'hidden',
                }}
              >
                {pendingTradesLoading || !pendingTradesCalled ? (
                  <Box
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} width='100%' height={28} />
                    ))}
                  </Box>
                ) : pendingTrades.length === 0 ? (
                  <Box sx={{ p: 1.5 }}>
                    <Typography
                      variant='bodySmall'
                      sx={{ color: sys.color.onSurfaceVariant }}
                    >
                      No active trades found
                    </Typography>
                    {(report.pendingSubTradeRequests || 0) > 0 && (
                      <Typography
                        variant='labelSmall'
                        sx={{ color: sys.color.warning, mt: 0.5 }}
                      >
                        Report shows {report.pendingSubTradeRequests} pending —
                        this count is stale. Trades may have been cancelled,
                        completed, or reconciled since the last portfolio
                        refresh.
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <>
                    <Box
                      display='flex'
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        bgcolor: sys.color.neutralHover,
                        borderBottom: '1px solid',
                        borderColor: sys.color.outlineVariant,
                      }}
                    >
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...TABLE_HEAD_SX,
                          width: '18%',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: sys.color.onSurfaceVariant,
                        }}
                      >
                        Side
                      </Typography>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...TABLE_HEAD_SX,
                          width: '20%',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: sys.color.onSurfaceVariant,
                        }}
                      >
                        Security
                      </Typography>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...TABLE_HEAD_SX,
                          width: '20%',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: sys.color.onSurfaceVariant,
                          textAlign: 'right',
                        }}
                      >
                        Amount
                      </Typography>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...TABLE_HEAD_SX,
                          width: '14%',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: sys.color.onSurfaceVariant,
                          textAlign: 'right',
                        }}
                      >
                        Qty
                      </Typography>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...TABLE_HEAD_SX,
                          width: '14%',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: sys.color.onSurfaceVariant,
                          textAlign: 'center',
                        }}
                      >
                        State
                      </Typography>
                      <Typography
                        variant='labelSmall'
                        sx={{
                          ...TABLE_HEAD_SX,
                          width: '14%',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          color: sys.color.onSurfaceVariant,
                          textAlign: 'right',
                        }}
                      >
                        Created
                      </Typography>
                    </Box>
                    <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                      {pendingTrades.map((t: any) => {
                        const isBuy = t.type === 'BUY';
                        const sideColor = isBuy
                          ? sys.color.positive
                          : sys.color.negative;
                        const stateColor =
                          t.state === 'REQUESTED'
                            ? sys.color.positive
                            : t.state === 'READY'
                              ? sys.color.primary
                              : sys.color.warning;
                        return (
                          <Box
                            key={t.id}
                            display='flex'
                            alignItems='center'
                            sx={{
                              px: 1.5,
                              py: 0.5,
                              borderBottom: '1px solid',
                              borderColor: sys.color.outlineVariant,
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { bgcolor: sys.color.neutralHover },
                            }}
                          >
                            <Box
                              sx={{
                                width: '18%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: sideColor,
                                  color: sys.color.onPrimary,
                                  ...LABEL_SX,
                                  fontWeight: 800,
                                }}
                              >
                                {isBuy ? '↑' : '↓'}
                              </Box>
                              <Typography
                                variant='bodySmall'
                                sx={{ ...VALUE_ROW_SX, color: sideColor }}
                              >
                                {t.type}
                              </Typography>
                            </Box>
                            <Box sx={{ width: '20%' }}>
                              <Typography
                                variant='bodySmall'
                                weight='bold'
                                sx={{ lineHeight: 1.2 }}
                              >
                                {t.financialProduct?.ticker || '—'}
                              </Typography>
                              <Typography
                                variant='labelSmall'
                                sx={{
                                  color: sys.color.onSurfaceVariant,
                                  lineHeight: 1.2,
                                  ...TRUNCATE_SX,
                                }}
                              >
                                {t.financialProduct?.translatedName?.en || ''}
                              </Typography>
                            </Box>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                ...VALUE_ROW_SX,
                                width: '20%',
                                textAlign: 'right',
                              }}
                            >
                              {formatMoney(t.moneyAllocatedCents)}
                            </Typography>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                ...TABULAR_SX,
                                width: '14%',
                                textAlign: 'right',
                                color: sys.color.onSurfaceVariant,
                              }}
                            >
                              {t.expectedQuantity
                                ? Number(t.expectedQuantity).toFixed(2)
                                : '—'}
                            </Typography>
                            <Box
                              sx={{
                                width: '14%',
                                display: 'flex',
                                justifyContent: 'center',
                              }}
                            >
                              <Typography
                                variant='labelSmall'
                                sx={{
                                  px: 0.75,
                                  py: 0.15,
                                  borderRadius: '4px',
                                  ...TABLE_HEAD_SX,
                                  fontWeight: 700,
                                  bgcolor: stateColor,
                                  color: sys.color.onPrimary,
                                  letterSpacing: '0.04em',
                                }}
                              >
                                {t.state}
                              </Typography>
                            </Box>
                            <Typography
                              variant='labelSmall'
                              sx={{
                                width: '14%',
                                textAlign: 'right',
                                color: sys.color.onSurfaceVariant,
                              }}
                            >
                              {t.createdAt
                                ? new Date(t.createdAt).toLocaleDateString(
                                    undefined,
                                    { month: 'short', day: 'numeric' },
                                  )
                                : '—'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                    {pendingTrades.length > 0 &&
                      (() => {
                        const totalBuys = pendingTrades.filter(
                          (t: any) => t.type === 'BUY',
                        );
                        const totalSells = pendingTrades.filter(
                          (t: any) => t.type !== 'BUY',
                        );
                        const buyTotal = totalBuys.reduce(
                          (s: number, t: any) =>
                            s + (t.moneyAllocatedCents || 0),
                          0,
                        );
                        const sellTotal = totalSells.reduce(
                          (s: number, t: any) =>
                            s + (t.moneyAllocatedCents || 0),
                          0,
                        );
                        return (
                          <Box
                            display='flex'
                            gap={2}
                            sx={{
                              px: 1.5,
                              py: 0.75,
                              bgcolor: sys.color.neutralHover,
                              borderTop: '1px solid',
                              borderColor: sys.color.outlineVariant,
                            }}
                          >
                            <Typography
                              variant='labelSmall'
                              sx={{
                                fontWeight: 700,
                                color: sys.color.positive,
                              }}
                            >
                              {totalBuys.length} Buy
                              {totalBuys.length !== 1 ? 's' : ''} ·{' '}
                              {formatMoney(buyTotal)}
                            </Typography>
                            <Typography
                              variant='labelSmall'
                              sx={{
                                fontWeight: 700,
                                color: sys.color.negative,
                              }}
                            >
                              {totalSells.length} Sell
                              {totalSells.length !== 1 ? 's' : ''} ·{' '}
                              {formatMoney(sellTotal)}
                            </Typography>
                            <Typography
                              variant='labelSmall'
                              sx={{
                                fontWeight: 700,
                                color: sys.color.onSurfaceVariant,
                                ml: 'auto',
                              }}
                            >
                              Net: {formatMoney(buyTotal - sellTotal)}
                            </Typography>
                          </Box>
                        );
                      })()}
                  </>
                )}
              </Box>
            </Box>
          )}

          {report.holdings && report.holdings.length > 0 && (
            <Box mb={2}>
              <Typography
                variant='labelSmall'
                sx={{ mb: 1, ...SECTION_HEADER_SX }}
              >
                Positions ({report.holdings.length})
              </Typography>
              <Box sx={{ overflowX: 'auto', ...BORDERED_BOX_SX }}>
                <Table
                  sx={{
                    backgroundColor: sys.color.surface,
                    ...ALIGNED_TABLE_SX,
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: sys.color.background }}>
                      {HOLDING_TABLE_COLUMNS.map((col) => {
                        const column = holdingTable.getColumn(col.id);
                        return (
                          <TableHeadCell
                            key={col.id}
                            number={col.number}
                            right={col.number}
                            onClick={column?.getToggleSortingHandler()}
                            sx={{
                              padding: '6px 16px',
                              width: col.width,
                              minWidth: col.minWidth,
                              ...SORTABLE_HEADER_SX,
                            }}
                          >
                            {col.label}
                            <SortIndicator
                              sorted={column?.getIsSorted() || false}
                            />
                          </TableHeadCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {holdingTable.getRowModel().rows.map((row) => {
                      const holding = row.original;
                      const isCash = holding.financialProduct?.isCash;
                      const isDrift = Math.abs(holding.driftPct) > 0.5;
                      const holdingId = holding.financialProduct?.id;
                      const canShowChart = !isCash && !!holdingId;
                      const isExpanded = expandedHoldingId === holdingId;
                      return (
                        <Fragment key={holdingId || row.id}>
                          <TableRow
                            sx={{
                              bgcolor: isDrift
                                ? sys.color.warningVariant
                                : 'transparent',
                              '&:hover': { bgcolor: sys.color.background },
                            }}
                          >
                            <HoldingCell w='20%'>
                              {isCash ? (
                                <BoldLabel>
                                  {holding.currency || 'USD'}
                                </BoldLabel>
                              ) : (
                                <Box
                                  sx={canShowChart ? { cursor: 'pointer' } : {}}
                                  onClick={
                                    canShowChart
                                      ? () =>
                                          setExpandedHoldingId(
                                            isExpanded ? null : holdingId,
                                          )
                                      : undefined
                                  }
                                >
                                  <Typography
                                    variant='bodyMedium'
                                    weight='bold'
                                    sx={{
                                      display: 'block',
                                      ...(canShowChart
                                        ? {
                                            textDecoration: 'underline',
                                            textDecorationStyle: 'dotted',
                                            textUnderlineOffset: '2px',
                                            '&:hover': {
                                              textDecorationStyle: 'solid',
                                              color: sys.color.primary,
                                            },
                                          }
                                        : {}),
                                    }}
                                  >
                                    {holding.financialProduct?.ticker}
                                  </Typography>
                                  <Typography
                                    variant='bodySmall'
                                    sx={{
                                      ...SECONDARY_TEXT_SX,
                                      display: 'block',
                                      ...TRUNCATE_SX,
                                    }}
                                  >
                                    {holding.financialProduct?.name}
                                  </Typography>
                                </Box>
                              )}
                            </HoldingCell>
                            <HoldingCell w='12%' n>
                              {isCash ? (
                                '-'
                              ) : (
                                <>
                                  {holding.quantity?.toFixed(2)}
                                  {holding.pendingQuantity !== 0 && (
                                    <Typography
                                      variant='labelSmall'
                                      component='span'
                                      sx={{
                                        color: sys.color.warning,
                                        display: 'block',
                                      }}
                                    >
                                      {holding.pendingQuantity > 0 ? '+' : ''}
                                      {holding.pendingQuantity?.toFixed(2)}
                                    </Typography>
                                  )}
                                </>
                              )}
                            </HoldingCell>
                            <HoldingCell w='12%' n>
                              {isCash
                                ? '-'
                                : formatMoneyValue(
                                    holding.financialProduct?.currentPriceCents,
                                    '$',
                                    2,
                                  )}
                            </HoldingCell>
                            <HoldingCell w='13%' n sx={{ fontWeight: 600 }}>
                              {formatMoneyValue(holding.valueCents, '$', 2)}
                            </HoldingCell>
                            <HoldingCell w='10%' n sx={{ fontWeight: 600 }}>
                              {holding.currentPct.toFixed(1)}%
                            </HoldingCell>
                            <HoldingCell w='10%' n>
                              {holding.targetPct.toFixed(1)}%
                            </HoldingCell>
                            <HoldingCell
                              w='11%'
                              n
                              sx={{
                                fontWeight: isDrift ? 700 : 400,
                                color: isDrift
                                  ? valueColor(sys.color, holding.driftPct)
                                  : sys.color.onSurface,
                              }}
                            >
                              {holding.driftPct > 0 ? '+' : ''}
                              {holding.driftPct.toFixed(1)}%
                            </HoldingCell>
                            <HoldingCell
                              w='12%'
                              n
                              sx={{
                                fontWeight: isDrift ? 700 : 400,
                                color: isDrift
                                  ? valueColor(sys.color, holding.driftCents)
                                  : sys.color.onSurface,
                              }}
                            >
                              {holding.driftCents > 0 ? '+' : ''}
                              {formatMoneyValue(holding.driftCents, '$', 2)}
                            </HoldingCell>
                          </TableRow>
                          {isExpanded && canShowChart && (
                            <TableRow>
                              <TableCell colSpan={8} sx={{ p: 0 }}>
                                <SecurityHistoryPanel
                                  financialProductId={holdingId}
                                  ticker={
                                    holding.financialProduct?.ticker || ''
                                  }
                                  securityName={
                                    holding.financialProduct?.name || ''
                                  }
                                  currency={holding.currency || 'CAD'}
                                  onClose={() => setExpandedHoldingId(null)}
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {report.modelPortfolioType === 'GOAL' &&
            report.subAccounts &&
            report.subAccounts.length > 0 && (
              <Box mb={2}>
                <Typography
                  variant='labelSmall'
                  sx={{ mb: 1, ...SECTION_HEADER_SX }}
                >
                  Account Breakdown ({report.subAccounts.length})
                </Typography>
                <Box display='flex' gap={1.5} flexWrap='wrap'>
                  {report.subAccounts.map((subAcct: any) => (
                    <Box
                      key={subAcct.subAccount.id}
                      sx={{
                        ...BORDERED_BOX_SX,
                        bgcolor: sys.color.surface,
                        p: 1.25,
                        minWidth: '200px',
                        flex: '1 1 200px',
                        maxWidth: '300px',
                      }}
                    >
                      <Box
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                        mb={0.5}
                      >
                        <Typography variant='labelSmall' sx={BOLD_SX}>
                          {subAcct.subAccount.account.type}
                        </Typography>
                        <Typography variant='labelSmall' sx={SECONDARY_TEXT_SX}>
                          {subAcct.subAccount.account.user.firstName ||
                            subAcct.subAccount.account.user.entityName}
                        </Typography>
                      </Box>
                      <Box display='flex' gap={2}>
                        <SubAcctStat
                          label='MV'
                          value={formatMoney(subAcct.marketValueCents)}
                        />
                        <SubAcctStat
                          label='Cash'
                          value={formatMoney(
                            subAcct.cashAvailableForTradeCents,
                          )}
                        />
                        {subAcct.pendingWithdrawCents > 0 && (
                          <SubAcctStat
                            label='Withdraw'
                            value={formatMoney(subAcct.pendingWithdrawCents)}
                            color={sys.color.warning}
                          />
                        )}
                        {(subAcct.pendingContributionCents ?? 0) > 0 && (
                          <SubAcctStat
                            label='Deposit'
                            value={formatMoney(
                              subAcct.pendingContributionCents ?? 0,
                            )}
                            color={sys.color.positive}
                          />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

          <Box display='flex' gap={1} justifyContent='flex-end'>
            <Button
              label='View Details'
              variant='outlined'
              size='sm'
              onClick={() => openClientDetails(report)}
            />
            <Button
              label='Generate Trades'
              variant='filled'
              size='sm'
              onClick={onShowTradeBlotter}
            />
          </Box>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default PortfolioRowDetail;
